"use client"

import styled from "@emotion/styled"
import { extractProperty, getNonUmbracoUrl, Link } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { heroCtaClicked } from "@eurodrive/utils/gtm"
import { IconButton, Typography } from "@mui/material"
import Image from "next/image"
import { useState } from "react"
import Container from "./Container"
import CustomBreadcrumbs from "./CustomBreadcrumbs"
import { Desktop, Mobile } from "./MediaQueries"
import ReactPlayerComponent from "./ReactPlayer"
import { sharedColors } from "./theme/colors"

const InnerLayout = styled.div<{
  borderRadius?: string
  compact?: boolean
  homepageAbout?: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  height: ${({ compact, homepageAbout }) =>
    homepageAbout ? "405px" : compact ? "240px" : "524px"};
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: ${({ compact, homepageAbout }) =>
      homepageAbout ? "405px" : compact ? "254px" : "600px"};
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    height: ${({ compact, homepageAbout }) =>
      homepageAbout ? "405px" : compact ? "254px" : "846px"};
  }
  border-radius: ${({ borderRadius }) => borderRadius};
  overflow: hidden;
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 100%
  );
  z-index: 1;
`

const Content = styled.div<{ compact?: boolean; homepageAbout?: boolean }>`
  /* padding: 20px 24px; */
  padding: ${({ homepageAbout, compact }) =>
    homepageAbout
      ? "64px 20px 24px 20px"
      : compact
        ? "0 0 40px 0"
        : "0 0 76px 0"};
  width: 100%;
  display: flex;
  flex-direction: ${({ homepageAbout }) => (homepageAbout ? "column" : "row")};
  gap: 40px;
  z-index: 1;
  justify-content: space-between;
  margin-top: auto;
  ${({ theme }) => theme.breakpoints.up("md")} {
    /* padding: 40px 80px; */
    padding: ${({ homepageAbout, compact }) =>
      homepageAbout ? "40px 80px" : compact ? "0 0 40px 0" : "0 0 120px 0"};
    /* align-items: flex-end; */
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    /* max-width: 700px; */
  }
`

const ControlsBox = styled.div`
  display: flex;
  justify-content: space-between;
  /* width: 100%; */
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const StyeldIconButton = styled(IconButton)`
  min-width: 0;
  padding: 0;
  z-index: 100;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`
const StyledTitle = styled(Typography)<{
  homepageAbout?: boolean
  component?: string
}>`
  font-size: ${({ homepageAbout }) => (homepageAbout ? "32px" : "40px")};
  line-height: 140%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: ${({ homepageAbout }) => (homepageAbout ? "40px" : "72px")};
    line-height: 130%;
  }
`

const StyledSubTitle = styled(Typography)<{
  homepageAbout?: boolean
  component?: string
}>`
  font-size: ${({ homepageAbout }) =>
    homepageAbout ? "16px" : "20px"} !important;
  line-height: 140%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 600px;
  }
`
const isVideo = (url: string): boolean => {
  return (
    url.includes(".mp4") ||
    url.includes(".webm") ||
    url.includes("youtube") ||
    url.includes("youtu.be")
  )
}
const GreyBreadcrumbs = styled.div<{ greyBreadcrumbs?: boolean }>`
  background-color: ${({ greyBreadcrumbs }) =>
    greyBreadcrumbs ? sharedColors.lightGrey : "transparent"};
  width: 100%;
`

export default function Banner({
  data,
  borderRadius,
  height,
  breadCrumbs,
  greyBreadcrumbs,
  compact,
  homepageAbout,
}: {
  data: any
  borderRadius?: string
  height?: string
  breadCrumbs?: {
    selfObject: { title: string; url: string }
    parents: { title: string; url: string }[]
  }
  greyBreadcrumbs?: boolean
  compact?: boolean
  homepageAbout?: boolean
}) {
  const [playing, setPlaying] = useState(true)
  const title = extractProperty(data, "title") as string
  const text =
    extractProperty(data, "text") || extractProperty(data, "subTitle")
  const mediaUrl = extractProperty(data, "media")
  const mediaForDesktop = extractProperty(data, "mediaForDesktop")
  const mediaForMobile = extractProperty(data, "mediaForMobile")
  const imageForDesktop = extractProperty(data, "imageForDesktop")
  const imageForMobile = extractProperty(data, "imageForMobile")
  const desktopImage = imageForDesktop || mediaForDesktop
  const mobileImage = imageForMobile || mediaForMobile
  const mediaId = extractProperty(data, "media")?.[0]?.id
  const buttonName = extractProperty(data, "button")?.[0]?.name
  const buttonUrl = extractProperty(data, "button")?.[0]?.url
  const buttonTarget = extractProperty(data, "button")?.[0]?.target

  const handleResume = () => {
    setPlaying(true)
  }
  const handlePause = () => {
    setPlaying(false)
  }

  const handleCtaClick = () => {
    if (buttonName) {
      heroCtaClicked(buttonName, homepageAbout ? "homepage_about" : "banner")
    }
  }

  const url = getUmbracoMediaUrl(desktopImage || mediaUrl)
  const isVideoUrl = isVideo(url)
  return (
    <>
      <InnerLayout
        borderRadius={borderRadius}
        compact={compact}
        homepageAbout={homepageAbout}
      >
        <Desktop>
          <ReactPlayerComponent
            playing={playing}
            source={getUmbracoMediaUrl(desktopImage || mediaUrl)}
            setPlaying={setPlaying}
            volume={false}
            setVolume={() => {}}
          />
        </Desktop>
        <Mobile>
          <ReactPlayerComponent
            playing={playing}
            source={mobileImage || mediaUrl}
            setPlaying={setPlaying}
            volume={false}
            setVolume={() => {}}
          />
        </Mobile>
        <Overlay />
        {homepageAbout ? (
          <Content compact={compact} homepageAbout={homepageAbout}>
            <TextBox>
              <StyledTitle
                variant="h1"
                color="white"
                component="h2"
                homepageAbout={homepageAbout}
              >
                {title}
              </StyledTitle>
              <StyledSubTitle
                variant="h4"
                component="p"
                color="white"
                homepageAbout={homepageAbout}
              >
                {text}
              </StyledSubTitle>
            </TextBox>

            {isVideoUrl || buttonName ? (
              <ControlsBox>
                {buttonName && (
                  <StyledLink
                    title={title}
                    href={getNonUmbracoUrl(buttonUrl)}
                    target={buttonTarget === "EXTERNAL" ? "_blank" : "_self"}
                    onClick={handleCtaClick}
                  >
                    <Typography variant="body1" color="white" fontWeight={500}>
                      {buttonName}
                    </Typography>
                    <Image
                      width={24}
                      height={24}
                      alt=""
                      src="/icons/left-arrow-alt.svg"
                    />
                  </StyledLink>
                )}
                {isVideoUrl ? (
                  <>
                    {!playing ? (
                      <StyeldIconButton onClick={handleResume}>
                        <Mobile>
                          <Image
                            src={"/icons/play-white.svg"}
                            width={20}
                            height={20}
                            alt="נגן"
                          />
                        </Mobile>
                        <Desktop>
                          <Image
                            src={"/icons/play-white.svg"}
                            width={30}
                            height={30}
                            alt="נגן"
                          />
                        </Desktop>
                      </StyeldIconButton>
                    ) : (
                      <StyeldIconButton onClick={handlePause}>
                        <Mobile>
                          <Image
                            src={"/icons/pause-white.svg"}
                            width={20}
                            height={20}
                            alt="עצור וידאו"
                          />
                        </Mobile>
                        <Desktop>
                          <Image
                            src={"/icons/pause-white.svg"}
                            width={30}
                            height={30}
                            alt="עצור וידאו"
                          />
                        </Desktop>
                      </StyeldIconButton>
                    )}
                  </>
                ) : null}
              </ControlsBox>
            ) : null}
          </Content>
        ) : (
          <Container paddingMobile heightFull>
            <Content compact={compact} homepageAbout={homepageAbout}>
              <TextBox>
                <StyledTitle variant="h1" component="h1" color="white">
                  {title}
                </StyledTitle>
                {text && (
                  <StyledSubTitle variant="h4" component="p" color="white">
                    {text}
                  </StyledSubTitle>
                )}
              </TextBox>

              {isVideoUrl || buttonName ? (
                <ControlsBox>
                  {buttonName && (
                    <StyledLink
                      href={getNonUmbracoUrl(buttonUrl)}
                      target={buttonTarget === "EXTERNAL" ? "_blank" : "_self"}
                      onClick={handleCtaClick}
                    >
                      <Typography
                        variant="body1"
                        color="white"
                        fontWeight={500}
                      >
                        {buttonName}
                      </Typography>
                      <Image
                        width={24}
                        height={24}
                        alt=""
                        src="/icons/left-arrow-alt.svg"
                      />
                    </StyledLink>
                  )}
                  {isVideoUrl ? (
                    <>
                      {!playing ? (
                        <StyeldIconButton onClick={handleResume}>
                          <Mobile>
                            <Image
                              src={"/icons/play-white.svg"}
                              width={20}
                              height={20}
                              alt="נגן"
                            />
                          </Mobile>
                          <Desktop>
                            <Image
                              src={"/icons/play-white.svg"}
                              width={30}
                              height={30}
                              alt="נגן"
                            />
                          </Desktop>
                        </StyeldIconButton>
                      ) : (
                        <StyeldIconButton onClick={handlePause}>
                          <Mobile>
                            <Image
                              src={"/icons/pause-white.svg"}
                              width={20}
                              height={20}
                              alt="עצור וידאו"
                            />
                          </Mobile>
                          <Desktop>
                            <Image
                              src={"/icons/pause-white.svg"}
                              width={30}
                              height={30}
                              alt="עצור וידאו"
                            />
                          </Desktop>
                        </StyeldIconButton>
                      )}
                    </>
                  ) : null}
                </ControlsBox>
              ) : null}
            </Content>
          </Container>
        )}
      </InnerLayout>

      {breadCrumbs && (
        <GreyBreadcrumbs greyBreadcrumbs={greyBreadcrumbs}>
          <Container paddingMobile>
            <CustomBreadcrumbs breadcrumbs={breadCrumbs} />
          </Container>
        </GreyBreadcrumbs>
      )}
    </>
  )
}
